import LegalLayout from "@/components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="June 1, 2026"
      testId="page-privacy"
    >
      <p>
        This Privacy Policy explains what personal information{" "}
        <strong>Facetra Design Studio Private Limited</strong> (<strong>"Facetra"</strong>,{" "}
        <strong>"we"</strong>) collects from users of{" "}
        <a href="https://facetra.studio">facetra.studio</a> (the <strong>"Service"</strong>),
        how we use it, who we share it with, and the choices you have. We comply with the{" "}
        <strong>Digital Personal Data Protection Act, 2023 (India)</strong> and follow
        principles from the GDPR for international users.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 You provide to us</h3>
      <ul>
        <li><strong>Account:</strong> name, email address, profile photo (via Google sign-in).</li>
        <li><strong>Waitlist / demo booking:</strong> name, email, company, role, message.</li>
        <li><strong>User Content:</strong> sketches, reference images, prompts, render selections, BOM edits, portfolio names.</li>
        <li><strong>Payments:</strong> billing name, email, payment method details — processed and stored by Razorpay. We only store the payment_id, order_id, amount, status and pack purchased.</li>
        <li><strong>Communications:</strong> messages you send us via email or in-app.</li>
      </ul>

      <h3>1.2 Collected automatically</h3>
      <ul>
        <li><strong>Usage data:</strong> pages visited, features used, credit transactions, generation counts, timestamps.</li>
        <li><strong>Device & log data:</strong> IP address, browser type, OS, referrer URL, error logs.</li>
        <li><strong>Cookies:</strong> a single HTTP-only session cookie (<code>session_token</code>) for authentication. We do not use third-party advertising cookies.</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To provide and operate the Service (generate renders, save portfolios, process payments).</li>
        <li>To authenticate you and protect against abuse (rate-limiting, brute-force protection).</li>
        <li>To send transactional emails (welcome, demo booking confirmations, studio-ready notifications, payment receipts).</li>
        <li>To improve the Service (analytics on feature usage — never tied to your generated content).</li>
        <li>To comply with legal obligations and respond to lawful requests.</li>
      </ul>

      <h2>3. AI Processing</h2>
      <p>
        Prompts, sketches, and reference images you submit are transmitted to our AI provider
        (currently Google Gemini, routed via Emergent's integration layer) solely to generate
        your requested render. Generated outputs are stored in our database and durable
        storage (MongoDB GridFS) under your account. <strong>We do not use your User Content
        to train third-party AI models</strong> outside the request/response cycle, and our
        provider agreements prohibit them from doing so for our customers' content.
      </p>

      <h2>4. Who We Share Information With</h2>
      <p>We share information only with service providers strictly necessary to operate Facetra:</p>
      <table>
        <thead>
          <tr><th>Provider</th><th>Purpose</th><th>Data</th></tr>
        </thead>
        <tbody>
          <tr><td>Google</td><td>Sign-in (OAuth)</td><td>email, profile</td></tr>
          <tr><td>Google Gemini / Emergent</td><td>AI image &amp; text generation</td><td>prompts, reference images</td></tr>
          <tr><td>Razorpay</td><td>Payment processing</td><td>billing name, email, payment method</td></tr>
          <tr><td>Resend</td><td>Transactional email</td><td>name, email</td></tr>
          <tr><td>MongoDB Atlas</td><td>Database hosting</td><td>all stored data, encrypted at rest</td></tr>
          <tr><td>Emergent (Kubernetes infra)</td><td>App hosting</td><td>app runtime, logs</td></tr>
        </tbody>
      </table>
      <p>
        We do not sell your personal information. We may disclose information to comply with
        valid legal requests (subpoenas, court orders) or to protect our rights and users'
        safety.
      </p>

      <h2>5. Data Retention</h2>
      <ul>
        <li><strong>Account &amp; User Content:</strong> retained while your account is active.</li>
        <li><strong>Generated renders &amp; portfolios:</strong> until you delete them or close your account.</li>
        <li><strong>Payment records:</strong> retained for 7 years as required by Indian tax law.</li>
        <li><strong>Logs (server, error, security):</strong> retained 90 days, then anonymised.</li>
      </ul>
      <p>
        You can request deletion of your account and associated data by emailing{" "}
        <a href="mailto:hello@facetra.studio">hello@facetra.studio</a>. We will action verified
        requests within 30 days, subject to retention required by law.
      </p>

      <h2>6. Your Rights</h2>
      <p>Under DPDP Act 2023 and applicable law, you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you</li>
        <li>Correct inaccurate data</li>
        <li>Erase your data (subject to legal retention)</li>
        <li>Withdraw consent at any time</li>
        <li>Grievance redressal — contact our DPO below</li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We protect your data with: HTTPS/TLS in transit, encryption at rest, HMAC-signed
        download URLs for image access, IP-based rate-limiting on admin endpoints, short-lived
        session tokens, and access controls limiting data to authorised personnel. No system
        is perfectly secure — if we detect a breach we will notify affected users without
        undue delay as required by law.
      </p>

      <h2>8. International Transfers</h2>
      <p>
        Some of our service providers may process data outside India. We rely on their
        contractual commitments (Standard Contractual Clauses where applicable) to ensure
        equivalent protection.
      </p>

      <h2>9. Children</h2>
      <p>
        The Service is not directed to users under 18. We do not knowingly collect data from
        children.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We will post any material change on this page and notify active users via email when
        appropriate. The "Last updated" date reflects the most recent revision.
      </p>

      <h2>11. Analytics & Cookies</h2>
      <p>
        Facetra uses <strong>Google Analytics 4</strong> (property <code>G-VFK32QM95F</code>)
        to understand which pages and features ateliers, designers and students engage with.
        We do <strong>not</strong> use advertising cookies or cross-site tracking. IP anonymisation
        is enabled on all measurement calls.
      </p>
      <p>
        We implement Google's <strong>Consent Mode v2</strong> in a "denied-by-default" posture:
        no analytics, ads, or personalisation storage is enabled until you explicitly
        accept the cookie banner. Your choice is remembered in your browser via a single
        <code>localStorage</code> key (<code>facetra_ga_consent</code> = <code>granted</code>
        or <code>denied</code>). Declining keeps every storage category off — GA4 will only
        receive anonymous, cookieless "modeled" pings that carry no identifiers.
      </p>
      <p>
        <strong>Beyond analytics we set:</strong>
      </p>
      <ul>
        <li>
          <code>session_token</code> — an HTTP-only session cookie required for authentication.
          Not a tracking cookie.
        </li>
        <li>
          <code>facetra_ref</code> — a short-lived cookie used only when you arrive from an
          affiliate vendor link, so we can credit the referral on signup. Cleared automatically
          on successful signup.
        </li>
      </ul>
      <p>
        <strong>Change your mind at any time.</strong> Use the "Cookie preferences" link in
        the footer of any page to clear <code>facetra_ga_consent</code> and re-open the banner.
        You can also block or clear cookies from your browser settings — the service will
        continue to work; only anonymised analytics will pause.
      </p>

      <h2>12. Contact — Data Protection Officer</h2>
      <p>
        <strong>Facetra Design Studio Private Limited</strong><br />
        Email: <a href="mailto:privacy@facetra.studio">privacy@facetra.studio</a><br />
        General: <a href="mailto:hello@facetra.studio">hello@facetra.studio</a>
      </p>
    </LegalLayout>
  );
}
